/**
 * Integrytas Group - UI Layout Delta Override Engine (ULD)
 * use_uld_layout - dual-layer runtime hydration hook.
 *
 * Step 1: the screen passes its code-provided core layout (the latest release).
 * Step 2: this fetches the active workspace's delta ledger and merges it on top.
 * Stale actions (keys the release removed) are skipped and reported once per
 * session via fire-and-forget telemetry. Hand the result to your renderer, then
 * to the existing personalization layer (per-user/role) if used.
 *
 * `useUldLayout` returns a FLAT layout (single-section pages); `useUldSectionedLayout`
 * returns ordered sections (multi-section pages). Both share one fetch + change-bus
 * via the internal `useUldDelta`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiDb } from '../lib/api_client';
import { asfWorkspaceCode } from '../lib/asf';
import { applyLayoutDelta, applySectionedLayout } from '../lib/uld_merge';
import type { UldLayoutItem, UldDeltaAction, UldSection, SectionDef } from '../lib/uld_merge';
import type { AuthSession } from '../types/auth';

// Session-level guard so each stale action only emits one telemetry warning.
const _warned = new Set<string>();

// Cross-component change bus. A successful design-mode save (FieldRenderer) calls
// emitUldChanged(pageCode, ws); every mounted useUldLayout for that page+workspace
// then refetches. The Multi-Tab Manager keeps pages mounted, so this is how a
// saved layout shows up without a full reload — on the page that saved it AND any
// other tab showing the same page. No per-page wiring needed.
type UldChangeListener = (key: string) => void;
const _changeListeners = new Set<UldChangeListener>();
const _changeKey = (pageCode: string | null | undefined, ws: string) => `${pageCode ?? ''}|${ws}`;

/** Broadcast that a layout delta was saved for this page/workspace. */
export function emitUldChanged(pageCode: string | null | undefined, workspaceCode = ''): void {
  const key = _changeKey(pageCode, workspaceCode);
  for (const fn of [..._changeListeners]) fn(key);
}

function resolveWs(opts?: { workspaceCode?: string | null }): string {
  return opts?.workspaceCode !== undefined ? (opts.workspaceCode ?? '') : (asfWorkspaceCode() || '');
}

/** Telemetry callback for stale (release-removed) delta actions. */
function useUldOnMissing(pageCode: string | null | undefined, ws: string) {
  return useCallback((action: UldDeltaAction) => {
    const sig = `${ws}|${pageCode}|${action.component_key}|${action.action}`;
    if (_warned.has(sig)) return;
    _warned.add(sig);
    // Best-effort telemetry; apiDb.rpc resolves (never rejects) with any error in `{error}`.
    void apiDb.rpc('uld_log_warning', {
      p_page_code: pageCode, p_workspace_code: ws, p_component_key: action.component_key, p_action_type: action.action,
    });
  }, [pageCode, ws]);
}

/**
 * Shared raw-delta fetch + change-bus subscription + reload. Resolves the tiered
 * layout (SYSTEM ++ active-ROLE ++ USER) via uld_resolve_layout and returns the
 * delta for the requested component ('' = the page form).
 */
function useUldDelta(pageCode: string | null | undefined, ws: string, enabled: boolean, componentKey = '') {
  const [delta, setDelta] = useState<UldDeltaAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick(t => t + 1), []);

  useEffect(() => {
    let live = true;
    if (!pageCode || !enabled) { setDelta([]); return; }
    setLoading(true); setError(null);
    void (async () => {
      try {
        const { data, error: e } = await apiDb.rpc<Record<string, UldDeltaAction[]>>(
          'uld_resolve_layout', { p_page_code: pageCode, p_workspace_code: ws });
        if (e) throw e;
        const arr = data && typeof data === 'object' ? data[componentKey] : undefined;
        if (live) setDelta(Array.isArray(arr) ? arr : []);
      } catch (err) {
        if (live) { setError((err as Error).message); setDelta([]); }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [pageCode, ws, enabled, componentKey, reloadTick]);

  // Refetch when any FieldRenderer on this page+workspace saves a layout change.
  useEffect(() => {
    const myKey = _changeKey(pageCode, ws);
    const onChange = (key: string) => { if (key === myKey) reload(); };
    _changeListeners.add(onChange);
    return () => { _changeListeners.delete(onChange); };
  }, [pageCode, ws, reload]);

  return { delta, loading, error, reload };
}

export interface UseUldLayout {
  layout: UldLayoutItem[];
  missing: UldDeltaAction[];
  loading: boolean;
  error: string | null;
  /** Re-fetch the delta. Call after saving a layout change — the Multi-Tab
   *  Manager keeps pages mounted, so a tab switch alone won't remount/refetch. */
  reload: () => void;
}

export function useUldLayout(
  pageCode: string | null | undefined,
  coreLayout: UldLayoutItem[],
  opts?: { workspaceCode?: string | null; enabled?: boolean },
): UseUldLayout {
  const enabled = opts?.enabled ?? true;
  const ws = resolveWs(opts);
  const { delta, loading, error, reload } = useUldDelta(pageCode, ws, enabled);
  const onMissing = useUldOnMissing(pageCode, ws);

  const { layout, missing } = useMemo(
    () => applyLayoutDelta(coreLayout, delta, onMissing),
    [coreLayout, delta, onMissing],
  );

  return { layout, missing, loading, error, reload };
}

// ── Tiered design (System / Role / User) ────────────────────────────────────

export type UldTier = 'SYSTEM' | 'ROLE' | 'USER';
export interface UldRoleOwner { role_id: number; internal_code: string; display_text: string }
export interface UldPageTiers { system: boolean; role: boolean; user: boolean; role_owner: UldRoleOwner | null }

function tiersToList(t: UldPageTiers | null): UldTier[] {
  if (!t) return [];
  return (['SYSTEM', 'ROLE', 'USER'] as UldTier[]).filter(k => t[k.toLowerCase() as 'system' | 'role' | 'user']);
}

/**
 * Which design tiers the current user may edit on a page, plus the role that owns
 * the page's ROLE design (if any). Drives the design editor's tier selector.
 */
export function useDesignTier(pageCode: string | null | undefined, ws = '') {
  const [tiers, setTiers] = useState<UldPageTiers | null>(null);
  const [editTier, setEditTier] = useState<UldTier | null>(null);

  useEffect(() => {
    let live = true;
    if (!pageCode) { setTiers(null); return; }
    void (async () => {
      const { data } = await apiDb.rpc<UldPageTiers>('uld_page_tiers', { p_page_code: pageCode, p_workspace_code: ws });
      if (!live) return;
      const t = (data && typeof data === 'object') ? data : { system: false, role: false, user: false, role_owner: null };
      setTiers(t);
      const avail = tiersToList(t);
      setEditTier(prev => (prev && avail.includes(prev) ? prev : (avail[0] ?? null)));
    })();
    return () => { live = false; };
  }, [pageCode, ws]);

  return {
    available: tiersToList(tiers),
    editTier,
    setEditTier,
    roleOwner: tiers?.role_owner ?? null,
    loading: tiers === null,
  };
}

// Session-cached "this user has at least one USER-tier delta" (visibility PRIMARY).
let _userHasPersonalization: boolean | null = null;

/** Write a component's delta to a tier via the single guarded RPC, then notify. */
export async function saveUldDesign(
  pageCode: string, componentKey: string, scopeType: UldTier, ws: string, delta: UldDeltaAction[],
): Promise<{ error: string | null }> {
  const { error } = await apiDb.rpc('uld_save_design', {
    p_page_code: pageCode, p_component_key: componentKey, p_scope_type: scopeType, p_workspace_code: ws, p_delta: delta,
  });
  if (!error) {
    if (scopeType === 'USER') _userHasPersonalization = true;
    emitUldChanged(pageCode, ws);
  }
  return { error: error ? error.message : null };
}

/**
 * Should the page's "Design" button be visible for this user?
 * PRIMARY: any USER-tier delta exists anywhere → show on all pages.
 * SECONDARY: else show where an assigned role has role- OR user-personalization
 *            enabled for this page. DEFAULT: hidden. Ungated if session/pageCode missing.
 */
export function useDesignButtonVisible(
  session: AuthSession | null | undefined,
  pageCode: string | null | undefined,
): boolean {
  const [primary, setPrimary] = useState<boolean>(_userHasPersonalization ?? false);
  useEffect(() => {
    if (_userHasPersonalization !== null) { setPrimary(_userHasPersonalization); return; }
    let live = true;
    void (async () => {
      const { data } = await apiDb.rpc<boolean>('uld_user_has_personalization');
      _userHasPersonalization = !!data;
      if (live) setPrimary(!!data);
    })();
    return () => { live = false; };
  }, []);

  if (!session || !pageCode) return true;
  if (primary) return true;
  const pa = session.effective_user.page_access.find(p => p.page_internal_code === pageCode);
  return !!(pa && (pa.can_personalize_role || pa.can_personalize_user));
}

/** Human-readable message for an uld_save_design RAISE. */
export function tierErrorMessage(raw: string, roleOwner: UldRoleOwner | null): string {
  if (raw.includes('ROLE_OWNED_BY')) return `This page's Role design is owned by ${roleOwner?.display_text ?? 'another role'} and can't be edited by your role.`;
  if (raw.includes('SYSTEM_REQUIRES_ADMIN')) return 'Only an administrator can edit the System layout.';
  if (raw.includes('ROLE_NOT_PERMITTED')) return 'Your roles are not permitted to personalize this page at the Role level.';
  if (raw.includes('USER_NOT_PERMITTED')) return 'You are not permitted to personalize this page.';
  return raw;
}

export interface UseUldSectionedLayout {
  sections: UldSection[];
  missing: UldDeltaAction[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useUldSectionedLayout(
  pageCode: string | null | undefined,
  coreLayout: UldLayoutItem[],
  sectionDefs: SectionDef[],
  opts?: { workspaceCode?: string | null; enabled?: boolean },
): UseUldSectionedLayout {
  const enabled = opts?.enabled ?? true;
  const ws = resolveWs(opts);
  const { delta, loading, error, reload } = useUldDelta(pageCode, ws, enabled);
  const onMissing = useUldOnMissing(pageCode, ws);

  const { sections, missing } = useMemo(
    () => applySectionedLayout(coreLayout, sectionDefs, delta, onMissing),
    [coreLayout, sectionDefs, delta, onMissing],
  );

  return { sections, missing, loading, error, reload };
}
